/**
 * mergeAddedAdapterIds — union an operator's persisted "added adapters" draft
 * into the wizard's current adapter-id list.
 *
 * Why this exists
 * ---------------
 * Adapters are shared/global (no `org_id` / `server_id` column). The only
 * authoritative org↔adapter link is a bank referencing the adapter
 * (bank.adapter_id -> channel -> server -> org), so the wizard hydrator seeds
 * `wizardState.adapterIds` from bank-referenced adapters only. Adapters the
 * operator added in the Adapters step but has NOT yet attached to a bank have
 * no such link — they live only in the per-org Adapters-step draft
 * (wizard_drafts). On resume they must be unioned back into `adapterIds` or
 * they vanish from the "Added Adapters" table even though the operator added
 * them.
 *
 * Contract
 * --------
 * - `restored` is operator-controlled persisted JSON, so accept ONLY a
 *   string[] of non-empty ids; anything else is ignored.
 * - The result de-duplicates against `current` and preserves order (current
 *   ids first, then newly-restored ids in their persisted order).
 * - Returns the SAME `current` reference when nothing new is added, so callers
 *   can cheaply skip a redundant state update / re-render.
 */
export default function mergeAddedAdapterIds(current: string[], restored: unknown): string[] {
  if (!Array.isArray(restored)) return current;

  const merged = [...current];
  restored.forEach((id) => {
    if (typeof id === "string" && id.length > 0 && !merged.includes(id)) {
      merged.push(id);
    }
  });

  return merged.length === current.length ? current : merged;
}
